import './TQServiceRequestResponseBottpmSheet.scss';

import React, {
  useMemo,
  useState,
} from 'react';

import { message } from 'antd';
import { BottomSheet } from 'react-spring-bottom-sheet';

import {
  resetCreateOrViewOfferModalState,
  setCreateOrViewOfferModalState,
} from '../../../../context/tqConnectServiceRequestReducer';
import {
  useUpdateTQServiceRequestResponse,
} from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import {
  useTQConnectServiceRequestById,
} from '../../../../hooks/queries/tqConnectQueries/service-requests';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  ITQCreateServiceRequestResponseBody,
  ITQServiceRequestResponse,
} from '../../../../types/tqConnectTypes/service-requests';
import ServiceRequestResponses from './ServiceRequestResponses';
import TQServiceRequestResponseDetails from './TQServiceRequestResponseDetails';

export interface TQServiceRequestResponseBottpmSheetProps {
  open: boolean;
  onDismiss: () => void;
  serviceRequestId: string;
  onViewProfile?: (userId: string) => void;
  isMyRequest?: boolean;
}

export type FormMode = 'create' | 'view' | 'confirm';

const initialFormValues: ITQCreateServiceRequestResponseBody = {
  description: '',
  attachments: [],
};

const TQServiceRequestResponseBottpmSheet: React.FC<
  TQServiceRequestResponseBottpmSheetProps
> = ({ open, onDismiss, serviceRequestId, isMyRequest = false }) => {
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState<FormMode>('view');
  const [formValues, setFormValues] =
    useState<ITQCreateServiceRequestResponseBody>(initialFormValues);
  const [attachments, setAttachments] = useState<File[]>([]);
  const { createOrViewOfferModalState } = useAppSelector(
    (state) => state.tqConnectServiceRequest,
  );

  const { response } = createOrViewOfferModalState;

  const { data: serviceRequestDetails } = useTQConnectServiceRequestById(
    serviceRequestId,
    false,
  );

  const { updateServiceRequestResponse, isPending: isAcceptingOrRejecting } =
    useUpdateTQServiceRequestResponse(serviceRequestId, response?._id || '');

  const handleDismiss = () => {
    dispatch(resetCreateOrViewOfferModalState());
    onDismiss();
  };

  const onChangeFormValues = <
    Key extends keyof ITQCreateServiceRequestResponseBody,
  >(
    key: Key,
    value: ITQCreateServiceRequestResponseBody[Key],
  ) => {
    setFormValues({
      ...formValues,
      [key]: value,
    });
  };

  const reset = () => {
    setMode('create');
    dispatch(resetCreateOrViewOfferModalState());
    setFormValues(initialFormValues);
    setAttachments([]);
  };

  const acceptOrRejectServiceRequest = async (
    status: 'accepted' | 'rejected',
  ) => {
    updateServiceRequestResponse(
      {
        serviceRequestId,
        serviceRequestResponseId: response?._id || '',
        data: { status },
      },
      {
        onSuccess: () => {
          message.success(`Offer ${status}`);
          reset();
        },
      },
    );
  };

  const showList = useMemo(() => !Boolean(response), [response]);
  const showDetails = useMemo(() => Boolean(response), [response]);

  return (
    <BottomSheet
      open={open}
      onDismiss={handleDismiss}
      header={null}
      className="tq-response-bottom-sheet">
      <div className="tq-response-bottom-sheet__content">
        {showList && (
          <ServiceRequestResponses
            isMyRequest={isMyRequest}
            serviceRequestId={serviceRequestId}
            onViewResponse={(respo: ITQServiceRequestResponse) => {
              dispatch(
                setCreateOrViewOfferModalState({
                  isOpen: true,
                  mode: 'view',
                  serviceRequestId,
                  respondedBy: respo.respondedBy,
                  response: respo,
                }),
              );
            }}
          />
        )}
        {showDetails && response ? (
          <TQServiceRequestResponseDetails
            mode="view"
            canAcceptOrReject={false}
            serviceRequestId={serviceRequestId}
            respondedBy={response?.respondedBy}
            response={response}
            formValues={formValues}
            onChangeFormValues={onChangeFormValues}
            attachments={attachments}
            setAttachments={setAttachments}
            serviceRequestDetails={serviceRequestDetails}
            onCloseDetails={handleDismiss}
            onSuccess={() => {
              if (mode === 'create') {
                reset();
              } else {
                setMode('confirm');
              }
            }}
            onAccept={() => setMode('confirm')}
            onReject={() => acceptOrRejectServiceRequest('rejected')}
            isRejecting={isAcceptingOrRejecting}
            reset={reset}
          />
        ) : null}
      </div>
    </BottomSheet>
  );
};

export default TQServiceRequestResponseBottpmSheet;
